import { Component } from '@theme/component';
import { clamp } from '@theme/utilities';

const STEP = 2;

/**
 * @typedef {object} PolarizedComparatorRefs
 * @property {HTMLElement} frame - The image frame, used for bounding-rect math.
 * @property {HTMLElement} handle - The draggable/focusable slider handle.
 * @property {HTMLElement} [glareLayer] - The clipped top image layer.
 */

/**
 * Drag-to-reveal before/after comparator. Dragging (or arrow-keying) the
 * handle moves a `--comparator-position` custom property (0-100), which the
 * glare layer's clip-path consumes to reveal the polarized image underneath.
 * @extends Component<PolarizedComparatorRefs>
 */
class PolarizedComparator extends Component {
  requiredRefs = ['frame', 'handle'];

  /** @type {number} */
  #position = 50;

  connectedCallback() {
    super.connectedCallback();

    const { frame, handle } = this.refs;

    frame.addEventListener('pointerdown', this.#handlePointerDown);
    handle.addEventListener('pointerdown', this.#handlePointerDown);

    this.#setPosition(this.#position);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.#setPosition(this.#position - STEP);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#setPosition(this.#position + STEP);
        break;
      case 'Home':
        event.preventDefault();
        this.#setPosition(0);
        break;
      case 'End':
        event.preventDefault();
        this.#setPosition(100);
        break;
    }
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    event.preventDefault();
    this.refs.handle.focus();
    this.#updateFromPointer(event);

    const controller = new AbortController();
    const { signal } = controller;

    /** @param {PointerEvent} moveEvent */
    const onPointerMove = (moveEvent) => {
      this.#updateFromPointer(moveEvent);
    };

    const onPointerUp = () => {
      controller.abort();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  /** @param {PointerEvent} event */
  #updateFromPointer(event) {
    const rect = this.refs.frame.getBoundingClientRect();
    if (rect.width === 0) return;

    const ratio = ((event.clientX - rect.left) / rect.width) * 100;
    this.#setPosition(ratio);
  }

  /** @param {number} value */
  #setPosition(value) {
    this.#position = clamp(value, 0, 100);

    this.style.setProperty('--comparator-position', `${this.#position}%`);
    this.refs.handle.setAttribute('aria-valuenow', Math.round(this.#position).toString());
  }
}

customElements.define('polarized-comparator-component', PolarizedComparator);
